/**
 * @lines98/client/lib/i18n/runtime — активный язык + `t()` (рантайм-движок i18n).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: ADR-0021 D-1 выбрал СВОЙ лёгкий i18n (vanilla, ноль
 * рантайм-зависимостей) вместо библиотеки (бандл-бюджет ADR-0001). Этот файл —
 * «ствол»: держит ОДИН активный язык на всё приложение и резолвит ключ→строку
 * через `t()` с подстановкой `{name}`-плейсхолдеров и fallback-цепочкой
 * `активный → en → ключ-как-есть`. Экраны/контролы вызывают `t()`, не зная про
 * загрузку каталогов.
 *
 * СВЯЗЬ СО СТОРОМ — ПОТОМ (T-I18N-2): сейчас активный язык живёт ЛОКАЛЬНО здесь
 * (`getLocale`/`setLocale`). T-I18N-2 заведёт поле `locale` в глобальном сторе и
 * свяжет его с `setLocale` (этот фасад останется, под капотом — подписка стора).
 * Поэтому store-поле тут НЕ заводим (scope T-I18N-1 — только машинерия).
 *
 * SCOPE: активный язык (get/set + lazy-подгрузка каталога при set), `t()` +
 *   подстановка + fallback. Без DOM, без стора, без рантайм-зависимостей.
 * NOT IN SCOPE: загрузка файлов каталогов (./catalogs), типы (./types), глобальный
 *   стор/реактивность (T-I18N-2), коды языков (`@lines98/shared/config/seo`).
 *
 * Cross-references: ADR-0021 D-1/D-2 (`docs/decisions/ADR-0021-i18n-global-language/README.md`);
 *   `@lines98/shared/config/seo` (UI_LANGS / DEFAULT_CONTENT_LANG).
 */

import { DEFAULT_CONTENT_LANG, type UiLang } from '@lines98/shared/config/seo';

import { loadCatalog, peekCatalog } from './catalogs.js';
import type { Catalog, TParams } from './types.js';

/**
 * Язык fallback-цепочки (ADR-0021 D-1: «активный → en → ключ»). Совпадает с
 * SEO-дефолтом (`DEFAULT_CONTENT_LANG = 'en'`) — единый источник, не хардкод.
 */
const FALLBACK_LANG: UiLang = DEFAULT_CONTENT_LANG;

/** Текущий активный язык приложения (по умолчанию — fallback `en`). */
let activeLocale: UiLang = FALLBACK_LANG;

/** Текущий активный язык. */
export function getLocale(): UiLang {
  return activeLocale;
}

/**
 * Сменить активный язык. Lazy-подгружает каталог нового языка (Vite code-split,
 * ./catalogs), затем делает его активным — после `await` каталог доступен `t()`
 * синхронно. Также гарантирует загрузку fallback-каталога (`en`), чтобы цепочка
 * fallback работала синхронно. Идемпотентно для уже активного языка.
 */
export async function setLocale(lang: UiLang): Promise<void> {
  // Fallback-каталог нужен всегда для цепочки — грузим заранее (кэш дедупит).
  if (lang !== FALLBACK_LANG) await loadCatalog(FALLBACK_LANG);
  await loadCatalog(lang);
  activeLocale = lang;
}

/**
 * Резолв строки по точечному ключу (`game.play`) из каталога активного языка с
 * подстановкой `{name}`-плейсхолдеров. Fallback-цепочка (ADR-0021 D-1):
 *   1) каталог активного языка,
 *   2) каталог `en` (fallback),
 *   3) сам ключ-как-есть (видимый сигнал «строка не заведена»).
 * Синхронно: каталоги активного и fallback языков должны быть предзагружены
 * (`setLocale` это делает до перерисовки). Если не загружены — шаг пропускается.
 */
export function t(key: string, params?: TParams): string {
  const fromActive = resolveKey(peekCatalog(activeLocale), key);
  const raw =
    fromActive ?? resolveKey(peekCatalog(FALLBACK_LANG), key) ?? key;
  return params ? interpolate(raw, params) : raw;
}

/** Пройти точечный путь по дереву каталога. Вернуть строку-лист или null. */
function resolveKey(catalog: Catalog | null, key: string): string | null {
  if (!catalog) return null;
  let node: string | Catalog = catalog;
  for (const part of key.split('.')) {
    if (typeof node !== 'object') return null;
    const next: string | Catalog | undefined = node[part];
    if (next === undefined) return null;
    node = next;
  }
  return typeof node === 'string' ? node : null;
}

/** Подставить `{name}`-плейсхолдеры. Неизвестные плейсхолдеры остаются как есть. */
function interpolate(template: string, params: TParams): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}
